import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Snackbar, Alert, IconButton, TextField
} from '@mui/material';
import { Save, Download, Close } from '@mui/icons-material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getToken, base_url } from '../config/config';


function CustomCheckbox({ label, checked, onChange , Sub}) {
  const handleClick = () => {
    onChange(!checked);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center space-x-1 hover:bg-gray-100 px-1"
    >
      <span className="flex items-center justify-center w-7 h-4 text-sm font-mono">
        [
        {checked ? <span className="text-black font-bold">✓</span> : <span>&nbsp;</span>}
        ]
      </span>
      {label && <span className="text-sm">{label}</span>}
      {Sub && <span className="text-[8px]">{Sub}</span>}
    </button>
  );
}

const VTNDocumentForm = ({ open, onClose, vtnData = null, anr, onSave }) => {
  const [formData, setFormData] = useState({
    documentNumber: 'FB V501.doc',
    page: '1',
    totalPages: '1',
    revision: '1',
    companyName: 'GABS GmbH',
    location: '01809 Heidenau',
    contact: '',
    phoneNumber: '',
    orderNumber: '',
    pickupDate: '',
    signature: '',
    date: new Date().toISOString().split('T')[0],
    // First parts section
    section1: {
      partDesignation: '',
      quantity: '',
      material: '',
      weight: '',
      vergueten: false,
      induktivhaerten: false,
      nitrieren: false,
      einsatzhaerten: false,
      isolieren: false,
      gluehen: false,
      other: '',
      otherCheck: '',
      ordered: false,
      drawing: false,
      combinedPosition: '',
      packagingType: '',
      totalWeight: ''
    },
    // Second parts section
    section2: {
      partDesignation: '',
      quantity: '',
      material: '',
      weight: '',
      vergueten: false,
      induktivhaerten: false,
      nitrieren: false,
      einsatzhaerten: false,
      isolieren: false,
      gluehen: false,
      other: '',
      ordered: false,
      drawing: false,
      combinedPosition: '',
      packagingType: '',
      totalWeight: ''
    }
  });

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const token = getToken();

  useEffect(() => {
    if (vtnData) {
      setFormData(prev => ({ ...prev, ...vtnData }));
    }
  }, [vtnData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSectionChange = (section, field, value) => {
    console.log('handleSectionChange', section, field, value);
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const generatePDF = async () => {
  const element = document.getElementById("vtn-document");
  if (!element) return;

  try {
    // Render element to canvas
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
    });

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Convert canvas to mm
    const imgWidth = canvas.width * 0.264583;
    const imgHeight = canvas.height * 0.264583;

    // Available space
    const margin = 5;
    const availableWidth = pdfWidth - margin * 2;
    const availableHeight = pdfHeight - margin * 2;

    // Scale to fit
    const scale = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);
    const scaledWidth = imgWidth * scale;
    const scaledHeight = imgHeight * scale;

    // Center
    const x = (pdfWidth - scaledWidth) / 2;
    const y = margin;

    pdf.addImage(
      canvas.toDataURL("image/png"),
      "PNG",
      x,
      y,
      scaledWidth,
      scaledHeight
    );

    return pdf;
  } catch (error) {
    throw error;
  }
};

  const downloadPDF = async () => {
    try {
      const pdf = await generatePDF();
      pdf.save(`VTN_${formData.orderNumber || 'Document'}_${formData.date}.pdf`);
      setSnackbar({
        open: true,
        message: 'PDF erfolgreich heruntergeladen!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Fehler beim Erstellen der PDF',
        severity: 'error'
      });
    }
  };

  const saveDocument = async () => {
    setSaving(true);
    try {
      const pdf = await generatePDF();
      const pdfBlob = pdf.output('blob');
      const formDataUpload = new FormData();
      formDataUpload.append('document', pdfBlob, `VTN_${formData.orderNumber || 'Document'}_${formData.date}.pdf`);
      formDataUpload.append('vtnData', JSON.stringify(formData));
      formDataUpload.append('documentType', 'VTN');
      formDataUpload.append('anr', anr);

      const url = vtnData && vtnData._id
        ? `${base_url}/v1/updateVTNDocument/${vtnData._id}`
        : `${base_url}/v1/createVTNDocument`;

      const method = vtnData && vtnData._id ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (!response.ok) throw new Error();
      const result = await response.json();
      setSnackbar({
        open: true,
        message: vtnData ? 'VTN Dokument aktualisiert!' : 'VTN Dokument erstellt!',
        severity: 'success'
      });
      onSave && onSave(result);
      setTimeout(onClose, 1200);
    } catch {
      setSnackbar({
        open: true,
        message: 'Fehler beim Speichern des VTN Dokuments',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const renderPartsSection = (sectionKey, posNumber) => {
    const section = formData[sectionKey];
    return (
      <>
      <div className="p-1 flex flex-wrap border-b-2 border-neutral-950 bg-gray-100">
                <div className="flex gap-10 w-full font-bold text-sm">
                  <span>Bauteile- und Wärmebehandlungskennndaten</span>
                  <span>
                    Pos.
                    <input
                      type="text"
                      className="border-b border-neutral-950 px-1 mx-1 w-12 text-center text-sm bg-gray-100"
                      placeholder=""
                    />
                    -
                    <input
                      type="text"
                      className="border-b border-neutral-950 px-1 mx-1 w-10 text-center text-sm bg-gray-100"
                      placeholder=""
                    />
                  </span>
                </div>
              </div>
              {/* Row.2 */}
              <div className="flex flex-wrap text-sm border-b-2 border-neutral-950">
                <div className="p-1 w-full md:w-1/5 border-r-2  border-neutral-950 bg-gray-100">
                  Teilebezeichnung/ Zeichnungs-Nr.:
                </div>
                <div className="flex-1 p-1">
                  <input
                    type="text"
                    className="w-full px-2 py-2 text-sm"
                    placeholder=""
                    value={section.partDesignation}
                    onChange={(e) => handleSectionChange(sectionKey, 'partDesignation', e.target.value)}
                  />
                </div>
              </div>
              {/* Row.3 */}
              <div className="flex flex-wrap border-b-2 border-neutral-950 text-sm">
                <div className="p-1 w-full md:w-1/5 border-r-2 border-neutral-950 bg-gray-100">
                  Menge:
                </div>
                <div className="p-1 w-full md:w-[16%] border-r-2 border-neutral-950">
                  <input
                    type="text"
                    className="w-full px-2 text-sm"
                    placeholder=""
                    value={section.quantity}
                    onChange={(e) => handleSectionChange(sectionKey, 'quantity', e.target.value)}
                  />
                </div>
                <div className="p-1 w-full md:w-[16%] border-r-2 border-neutral-950 bg-gray-100">
                  Material:
                </div>
                <div className="p-1 w-full md:w-[16%] border-r-2 border-neutral-950">
                  <input
                    type="text"
                    className="w-full px-2 text-sm"
                    placeholder=""
                    value={section.material}
                onChange={(e) => handleSectionChange(sectionKey, 'material', e.target.value)}
                  />
                </div>
                <div className="p-1 w-full md:w-[16%] border-r-2 border-neutral-950 bg-gray-100">
                  Gewicht/ Kg:
                </div>
                <div className="p-1 w-full md:w-[16%]">
                  <input
                    type="text"
                    className="w-full px-2 text-sm"
                    placeholder=""
                    value={section.weight}
                onChange={(e) => handleSectionChange(sectionKey, 'weight', e.target.value)}
                  />
                </div>
              </div>
              {/* Row.4 */}
              <div className="flex flex-wrap text-sm border-b-2 border-neutral-950">
                <div className="p-1 w-full md:w-1/5 border-r-2  border-neutral-950 bg-gray-100">
                  Behandlung:
                </div>
                <div className="p-1 w-full md:w-2/3">
                  <CustomCheckbox
                    label="Vergüten"
                    checked={section.vergueten}
                    onChange={(checked) => handleSectionChange(sectionKey, 'vergueten', checked)}
                  />
                  <CustomCheckbox
                    label="Induktivhärten"
                    checked={section.induktivhaerten}
                    onChange={(checked) => handleSectionChange(sectionKey, 'induktivhaerten', checked)}
                  />
                  <CustomCheckbox
                    label="Nitrieren"
                    checked={section.nitrieren}
                    onChange={(checked) => handleSectionChange(sectionKey, 'nitrieren', checked)}
                  />
                  <CustomCheckbox
                    label="Einsatzhärten"
                    checked={section.einsatzhaerten}
                    onChange={(checked) => handleSectionChange(sectionKey, 'einsatzhaerten', checked)}
                  />
                  <CustomCheckbox
                    label="Isolieren/Abdecken mit Paste"
                    checked={section.isolieren}
                    onChange={(checked) => handleSectionChange(sectionKey, 'isolieren', checked)}
                  />
                  <CustomCheckbox
                    label="Glühen"
                    checked={section.gluehen}
                    onChange={(checked) => handleSectionChange(sectionKey, 'gluehen', checked)}
                  />
                  <CustomCheckbox
                    label=""
                    checked={section.otherCheck}
                    onChange={(checked) => handleSectionChange(sectionKey, 'otherCheck', checked)}
                  />
                  <input
                    type="text"
                    className="border-b border-neutral-950 px-1 w-12 text-sm"
                    placeholder=""
                    value={section.other}
                    onChange={(e) => handleSectionChange(sectionKey, 'other', e.target.value)}
                  />
                </div>
              </div>
              {/* Row.5 */}
              <div className="flex flex-wrap text-sm border-b-2 border-neutral-950">
                <div className="p-1 w-full md:w-1/5 border-r-2 border-neutral-950 bg-gray-100">
                  Sollvorgaben nach
                  <CustomCheckbox
                    label="wie bestellt"
                    checked={section.ordered}
                    onChange={(checked) => handleSectionChange(sectionKey, 'ordered', checked)}
                  />
                  <CustomCheckbox
                    label="Zeichnung"
                    Sub="belgelegt"
                    checked={section.drawing}
                    onChange={(checked) => handleSectionChange(sectionKey, 'drawing', checked)}
                  />
                </div>
                <div className="flex-1 p-1">
                  <textarea
                    className="w-full px-2 py-2 text-sm resize-y min-h-[60px]"
                    placeholder=""
                  />
                </div>
              </div>
              <div className="flex flex-wrap text-sm border-b-2 border-neutral-950 bg-gray-100">
                <div className="p-1 w-full md:w-1/5 border-r-2  border-neutral-950"></div>
                <div className="p-1 w-full md:w-[60%]">
                  Zusammengefasste{" "}
                  <span>
                    Pos.
                    <input
                      type="text"
                      className="border-b border-neutral-950 px-1 mx-1 w-12 text-center text-sm bg-gray-100"
                      placeholder=""
                      value={section.combinedPosition}
                    onChange={(e) => handleSectionChange(sectionKey, 'combinedPosition', e.target.value)}
                    />
                  </span>{" "}
                  mit gleichem Werkstoff und bei gleicher Wärmebehandlung
                </div>
              </div>
              <div className="p-1 flex flex-wrap text-sm border-b-2 border-neutral-950 bg-gray-100 text-center">
                <div className="w-full md:w-[15%]">Verpackungsart:</div>
                <div className="w-full md:w-[30%]">
                  <input
                    type="text"
                    className="w-full px-2 text-sm bg-gray-100"
                    placeholder=""
                    value={section.packagingType}
                    onChange={(e) => handleSectionChange(sectionKey, 'packagingType', e.target.value)}
                  />
                </div>
                <div className="w-full md:w-[15%]">Gesamtgewicht:</div>
                <div className="w-full md:w-[30%]">
                  <input
                    type="text"
                    className="w-full px-2 text-sm bg-gray-100"
                    placeholder=""
                    value={section.totalWeight}
                    onChange={(e) => handleSectionChange(sectionKey, 'totalWeight', e.target.value)}
                  />
                </div>
              </div></>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ "& .MuiDialog-paper": { height: "95vh" } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {vtnData
              ? "VTN Dokument bearbeiten"
              : "Neues VTN Dokument erstellen"}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ height: "calc(95vh - 120px)", overflow: "auto" }}>
          <Paper
            id="vtn-document"
            sx={{
              backgroundColor: "#fff",
              color: "#000",
              fontFamily: "Arial, sans-serif",
              fontSize: "12px",
              maxWidth: "210mm",
              margin: "0 auto",
            }}
          >
            {/* First section - VTN Logo and Formblatt and other fields*/}
            <div className="m-1 mb-4 border-2 border-neutral-950">
              {/* Row.1 */}
              <div className="flex flex-wrap border-b-2 border-neutral-950">
                <div className="w-full md:w-1/3 p-3 border-r-2 border-neutral-950">
                  <div className="font-bold text-xl flex items-center">
                    <img src="/assets/VTN-logo.png" alt="VTN Logo" className="h-6 mr-2" />
                  </div>
                </div>
                <div className="w-full md:w-2/3 p-3">
                  <div className="text-center font-bold text-lg">Formblatt</div>
                </div>
              </div>
              {/* Row.2 */}
              <div className="flex flex-wrap border-b-2 border-neutral-950 text-center text-sm">
                <div className="w-full md:w-1/3 border-r-2 border-neutral-950 bg-gray-100">
                  Nr.
                </div>
                <div className="w-full md:w-1/3 border-r-2 border-neutral-950 bg-gray-100">
                  Seite
                </div>
                <div className="w-full md:w-1/3 bg-gray-100">Rev.</div>
              </div>
              {/* Row.3 */}
              <div className="flex flex-wrap border-b-2 border-neutral-950 text-center text-sm">
                <div className="w-full md:w-1/3 border-r-2 border-neutral-950">
                  FB V501.doc
                </div>
                <div className="flex flex-wrap w-full md:w-1/3">
                  <div className="w-full md:w-1/3 border-r-2 border-neutral-950">
                    1
                  </div>
                  <div className="w-full md:w-1/3 border-r-2 border-neutral-950">
                    von
                  </div>
                  <div className="w-full md:w-1/3 border-r-2 border-neutral-950">
                    1
                  </div>
                </div>
                <div className="w-full md:w-1/3 ">1</div>
              </div>
              {/* Row.4 */}
              {/* Row.4 */}
 <div className="flex flex-wrap">
  <div className="w-full text-center font-bold text-lg sm:text-xl px-2 py-1 overflow-hidden">
    <span className="break-words leading-tight">
      Ersatzlieferschein / Bestellung
    </span>
  </div>
</div>
            </div>

            {/* Second section - address */}
            <div className="m-1 mb-4 border-2 border-neutral-950">
              {/* Row.1 */}
              <div className="p-1 flex flex-wrap border-b-2 border-neutral-950 bg-gray-100">
                <div className="w-full font-bold text-sm">
                  Auftraggeber der Wärmebehandlung
                </div>
              </div>
              {/* Row.2 */}
              <div className="flex flex-wrap border-neutral-950 text-sm">
                <div className="p-1 w-full md:w-1/6 border-r-2 border-b-2 border-neutral-950 bg-gray-100">
                  Firmenname:
                </div>
                <div className="p-1 w-full md:w-1/3 border-r-2 border-b-2 border-neutral-950">
                  GABS GmbH
                </div>
                <div className="p-1 w-full md:w-1/6 ">Abholung am:</div>
                <input
                    type="date"
                    value={formData.pickupDate}
                    className="px-1 text-sm"
                    onChange={(e) => handleInputChange('pickupDate', e.target.value)}
                  />
              </div>
              {/* Row.3 */}
              <div className="flex flex-wrap border-neutral-950 text-sm">
                <div className="p-1 w-full md:w-1/6 border-r-2 border-b-2 border-neutral-950 bg-gray-100">
                  PLZ/ Ort:
                </div>
                <div className="p-1 w-full md:w-1/3 border-r-2 border-b-2 border-neutral-950">
                  01809 Heidenau
                </div>
              </div>
              {/* Row.4 */}
              <div className="flex flex-wrap border-neutral-950 text-sm">
                <div className="p-1 w-full md:w-1/6 border-r-2 border-b-2 border-neutral-950 bg-gray-100">
                  Kontakt:
                </div>
                <div className="p-1 w-full md:w-1/3 border-r-2 border-b-2 border-neutral-950">
                  <input
                    type="text"
                    className="w-full px-2 text-sm"
                    placeholder=""
                    value={formData.contact}
                    onChange={(e) => handleInputChange('contact', e.target.value)}
                  />
                </div>
                <div className="p-1 w-full md:w-1/6 ">Unterschrift:</div>
                <div className="p-1 w-full md:w-1/3">
                  <input
                    type="text"
                    className="w-full px-2 text-sm"
                    placeholder=""
                    value={formData.signature}
                    onChange={(e) => handleInputChange('signature', e.target.value)}
                  />
                </div>
              </div>
              {/* Row.5 */}
              <div className="flex flex-wrap border-neutral-950 text-sm border-b-2 border-neutral-950">
                <div className="p-1 w-full md:w-1/6 border-r-2 border-neutral-950 bg-gray-100">
                  Tel.-Nr.:
                </div>
                <div className="p-1 w-full md:w-1/3 border-r-2 border-neutral-950">
                  <input
                    type="text"
                    className="w-full px-2 text-sm"
                    placeholder=""
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  />
                </div>
                <div className="p-1 w-full md:w-1/6">Datum:</div>
                <input
                    type="date"
                    value={formData.date}
                    className="px-1 text-sm"
                    onChange={(e) => handleInputChange('date', e.target.value)}
                  />
              </div>
              <div className="flex flex-wrap text-sm ">
                <div className="p-1 w-full md:w-1/6 border-r-2 border-neutral-950 bg-gray-100">
                  Bestell-Nr.:
                </div>
                <div className="flex-1 p-1">
                  <input
                    type="text"
                    className="w-full px-2 text-sm"
                    placeholder=""
                    value={formData.orderNumber}
                    onChange={(e) => handleInputChange('orderNumber', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Third section - futher details */}
            <div className="m-1 border-2 border-neutral-950">
              {renderPartsSection('section1', '1')}
              {/* divider */}
              <div className="p-2 flex flex-wrap border-b-2 border-neutral-950 bg-gray-100"></div>
              {renderPartsSection('section2', '2')}
            </div>

            {/* footer */}
            <div className="mx-2 mb-6">
              <div className="w-full text-[11px]">
                Für die Ausführung der Arbeiten gelten ausschließlich unsere
                Zahlungs- und Lieferungsbedingungen, die Sie in unseren AGB´s
                unter <a>www.ihi-vtn.com</a> einsehen können.
              </div>
            </div>

            <div
              style={{
                fontSize: "12px",
                textAlign: "right",
                marginTop: "10px"
              }}
            >
              Seite 1/ 1
            </div>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined">
          Schließen
        </Button>
        <Button
          onClick={downloadPDF}
          variant="outlined"
          startIcon={<Download />}
        >
          PDF herunterladen
        </Button>
        <Button
          onClick={saveDocument}
          variant="contained"
          startIcon={<Save />}
          disabled={saving}
        >
          {saving ? "Speichert..." : vtnData ? "Aktualisieren" : "Speichern"}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default VTNDocumentForm;